import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { z } from 'zod'
import { UnsavedChangesDialog } from '../../../components/UnsavedChangesDialog'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges'
import { supabase } from '../../../lib/supabase'

interface AppSettingRow {
  key: string
  value: string
  description: string | null
  created_at: string
  updated_at: string
}

type FormValues = Record<string, string>

const settingSchema = z.string().min(1, 'Value is required')

export default function AppSettingsPage() {
  const queryClient = useQueryClient()
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [formValues, setFormValues] = useState<FormValues>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState<Record<string, boolean>>({})

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['app_settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .order('key', { ascending: true })
      if (error) throw error
      return data as AppSettingRow[]
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from('app_settings')
        .update({ value })
        .eq('key', key)
      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['app_settings'] })
      setSaved((prev) => ({ ...prev, [variables.key]: true }))
      setTimeout(() => {
        setSaved((prev) => ({ ...prev, [variables.key]: false }))
      }, 2000)
      setEditingKey(null)
    },
  })

  const isSettingDirty = useMemo((): boolean => {
    if (!editingKey) return false
    const current = settings.find((s) => s.key === editingKey)
    if (!current) return false
    return (
      formValues[editingKey] !== undefined &&
      formValues[editingKey] !== current.value
    )
  }, [editingKey, formValues, settings])

  const { isBlocked, proceed, reset } = useUnsavedChanges(isSettingDirty)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false)
  const [pendingSetting, setPendingSetting] = useState<AppSettingRow | null>(
    null,
  )

  const startEdit = (setting: AppSettingRow) => {
    setEditingKey(setting.key)
    setFormValues((prev) => ({ ...prev, [setting.key]: setting.value }))
    setErrors((prev) => ({ ...prev, [setting.key]: '' }))
  }

  const cancelEdit = () => {
    setEditingKey(null)
    setErrors({})
  }

  const handleStartEdit = (setting: AppSettingRow): void => {
    if (isSettingDirty && editingKey !== setting.key) {
      setPendingSetting(setting)
    } else {
      startEdit(setting)
    }
  }

  const handleCancelEdit = (): void => {
    if (isSettingDirty) {
      setShowDiscardConfirm(true)
    } else {
      cancelEdit()
    }
  }

  const handleConfirmDiscard = (): void => {
    setShowDiscardConfirm(false)
    cancelEdit()
  }

  const handleConfirmPendingSetting = (): void => {
    if (pendingSetting) {
      startEdit(pendingSetting)
      setPendingSetting(null)
    }
  }

  const handleSave = (key: string) => {
    const value = formValues[key] ?? ''
    const result = settingSchema.safeParse(value)
    if (!result.success) {
      setErrors((prev) => ({
        ...prev,
        [key]: result.error.errors[0]?.message ?? 'Invalid value',
      }))
      return
    }
    setErrors((prev) => ({ ...prev, [key]: '' }))
    updateMutation.mutate({ key, value })
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-bold text-2xl text-gray-900">App Settings</h1>
        <p className="mt-1 text-gray-500 text-sm">
          Database-level configuration. Changes take effect immediately.
        </p>
      </div>

      {isLoading ? (
        <div className="text-gray-500 text-sm">Loading settings…</div>
      ) : settings.length === 0 ? (
        <div className="text-gray-500 text-sm">No settings found.</div>
      ) : (
        <div className="space-y-4">
          {settings.map((setting) => (
            <Card key={setting.key} className="p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  <p className="font-mono font-semibold text-gray-800 text-sm">
                    {setting.key}
                  </p>
                  {setting.description && (
                    <p className="mt-0.5 text-gray-500 text-xs">
                      {setting.description}
                    </p>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-2 sm:max-w-xs">
                  {editingKey === setting.key ? (
                    <>
                      <Label
                        htmlFor={`input-${setting.key}`}
                        className="sr-only"
                      >
                        {setting.key}
                      </Label>
                      <Input
                        id={`input-${setting.key}`}
                        value={formValues[setting.key] ?? setting.value}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            [setting.key]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSave(setting.key)
                          if (e.key === 'Escape') handleCancelEdit()
                        }}
                        autoFocus
                      />
                      {errors[setting.key] && (
                        <p className="text-red-500 text-xs">
                          {errors[setting.key]}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSave(setting.key)}
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={updateMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-gray-900 text-sm">
                        {saved[setting.key] ? (
                          <span className="text-green-600">Saved!</span>
                        ) : (
                          setting.value
                        )}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStartEdit(setting)}
                      >
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <UnsavedChangesDialog
        isOpen={isBlocked}
        title="Unsaved Changes"
        description="You have unsaved changes in setting value. Are you sure you want to leave without saving? Your changes will be lost."
        confirmText="Leave without saving"
        cancelText="Stay on page"
        onConfirm={proceed}
        onCancel={reset}
      />

      <UnsavedChangesDialog
        isOpen={showDiscardConfirm}
        title="Discard Setting Changes?"
        description="You have unsaved changes in this setting. Are you sure you want to discard them?"
        confirmText="Discard changes"
        cancelText="Keep editing"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
      />

      <UnsavedChangesDialog
        isOpen={pendingSetting !== null}
        title="Discard Setting Changes?"
        description="You have unsaved changes in the current setting. Are you sure you want to discard them and edit another setting?"
        confirmText="Discard and switch"
        cancelText="Keep editing"
        onConfirm={handleConfirmPendingSetting}
        onCancel={() => setPendingSetting(null)}
      />
    </div>
  )
}
